import { PET_DEMOS_SPECIES } from '../../constants';

// Counts pets by species using their demographic data.
const countBySpecies = (pets) => {
  const safePets = Array.isArray(pets) ? pets : [];
  const countOf = (species) => safePets.filter(
    (pet) => pet.demographicData?.species === species,
  ).length;

  return {
    cats: countOf(PET_DEMOS_SPECIES.cat),
    dogs: countOf(PET_DEMOS_SPECIES.dog),
  };
};

/**
 * Removal dates the user still has to resolve. Removals already covered by
 * pets that were ineligible in a prior term are consumed (earliest date
 * first) and dropped, since those pets never show up as removal candidates.
 *
 * @param {Array} priorIneligiblePets - Pets of this term whose previous-term
 *   pet was ineligible
 * @param {Array} removalDates - Entries shaped { date, cats, dogs }
 * @returns {Array} Removal dates with the covered counts subtracted
 */
const getPendingRemovalDates = ({
  priorIneligiblePets,
  removalDates,
}) => {
  const safeRemovalDates = Array.isArray(removalDates) ? removalDates : [];
  const { cats, dogs } = countBySpecies(priorIneligiblePets);
  let remainingCats = cats;
  let remainingDogs = dogs;

  return safeRemovalDates
    .map((removalDate) => {
      const removalCats = Number(removalDate.cats) || 0;
      const removalDogs = Number(removalDate.dogs) || 0;
      const coveredCats = Math.min(remainingCats, removalCats);
      const coveredDogs = Math.min(remainingDogs, removalDogs);

      remainingCats -= coveredCats;
      remainingDogs -= coveredDogs;

      return {
        ...removalDate,
        cats: removalCats - coveredCats,
        dogs: removalDogs - coveredDogs,
      };
    })
    .filter((removalDate) => removalDate.cats > 0 || removalDate.dogs > 0);
};

/**
 * Builds the request body for submitting pet demographics to the backend API.
 *
 * @param {Object} options - Configuration object
 * @param {string} options.policyDisplayNumber - The policy certificate number
 * @param {Object} options.declarations - User agreements and consents
 *   (agree, caslAgree, isEconsentSubmitted)
 * @param {Map} options.coveragePeriodPetsData - Map of coverage period data,
 *   keyed by groupId, containing pet information per coverage period/term
 * @param {Array} options.needsPetDemographics - Original coverage periods from
 *   NeedsPetDemographics, used to look up renewal pets for ineligible injection
 * @returns {Object} Formatted request body ready for API submission
 *   - employeeCert: Contains declarations and demographics array
 *   - demographics: Array of coverage period data with formatted pet info
 *   - PolicyDisplayNumber: The policy number
 */
const buildPetDemographicsBody = ({
  policyDisplayNumber,
  declarations,
  coveragePeriodPetsData,
  needsPetDemographics = [],
}) => {
  const historicalIneligibles = Array.from(coveragePeriodPetsData.values())
    .flatMap((data) => data.ineligiblePets);

  const demographics = Array.from(coveragePeriodPetsData.entries()).map(
    ([groupId, periodData]) => {
      const {
        allPets,
        employeeId,
        policyTerm,
      } = periodData;

      const existingIds = new Set(
        allPets.map((pet) => pet.employeeDependentId),
      );

      const injectedPets = [];
      const priorIneligibles = historicalIneligibles.filter(
        (ineligPet) => ineligPet.policyTerm < policyTerm,
      );
      const renewalPets = needsPetDemographics
        .filter((period) => period.policyTerm === policyTerm)
        .flatMap((period) => period.pets);
      // Removals the backend already expects for this term.
      const pendingRemovals = needsPetDemographics
        .filter((period) => period.policyTerm === policyTerm)
        .flatMap((period) => period.removalDates || [])
        .map((removalDate) => ({ ...removalDate }));

      priorIneligibles.forEach((ineligPet) => {
        renewalPets.forEach((renewalPet) => {
          const parentId = renewalPet.parentEmployeeDependentId;
          const rootId = renewalPet.rootEmployeeDependentId;
          const matchesInelig = parentId === ineligPet.Id
            || rootId === ineligPet.Id;

          if (matchesInelig
            && !existingIds.has(renewalPet.employeeDependentId)) {
            existingIds.add(renewalPet.employeeDependentId);

            const speciesKey = ineligPet.Specie === PET_DEMOS_SPECIES.dog
              ? 'dogs'
              : 'cats';

            const matchingRemoval = pendingRemovals.find(
              (removalDate) => removalDate[speciesKey] > 0,
            );

            // Mark this removal as used so the next ineligible pet of the
            // same species takes the following date instead of this one.
            if (matchingRemoval) {
              matchingRemoval[speciesKey] -= 1;
            }

            injectedPets.push({
              dependentData: {
                age: ineligPet.age,
                breed: ineligPet.breed,
                breedId: ineligPet.breedId,
                dateOfBirth: ineligPet.dateOfBirth,
                petName: ineligPet.Name,
                species: ineligPet.Specie,
                weight: ineligPet.weight,
              },
              employeeDependentId: renewalPet.employeeDependentId,
              ineligible: true,
              orcaPetId: renewalPet.orcaPetId,
              removalDate: matchingRemoval?.date ?? null,
              replaceDependentId: null,
            });
          }
        });
      });

      const pets = [
        ...allPets.map((pet) => {
          const demographicsData = pet.demographicData;

          return {
            dependentData: {
              age: demographicsData.age,
              breed: demographicsData.breed,
              breedId: demographicsData.breedId,
              dateOfBirth: demographicsData.dateOfBirth,
              petName: demographicsData.petName,
              species: demographicsData.species,
              weight: demographicsData.weight,
            },
            employeeDependentId: pet?.isReplacedPet
              ? null : pet.employeeDependentId,
            ineligible: !!demographicsData.isIneligible,
            orcaPetId: pet.orcaPetId,
            removalDate: demographicsData.isRemoved
              ? pet.removalDate : null,
            replaceDependentId: pet?.isReplacedPet
              ? pet.employeeDependentId : null,
          };
        }),
        ...injectedPets,
      ];

      return {
        employeeId,
        groupId,
        pets,
        policyTerm,
      };
    },
  );

  // Return API request payload with declarations and formatted demographics
  return {
    employeeCert: {
      declarations: {
        agree: !!declarations?.agree,
        caslAgree: !!declarations?.caslAgree,
        isEconsentSubmitted: !!declarations?.isEconsentSubmitted,
      },
      demographics,
    },
    PolicyDisplayNumber: policyDisplayNumber,
  };
};

/**
 * How many pets per species are cancelled effective a term's coverage
 * start date, per the term's own removalDates entry (if any).
 */
const getCancelledCounts = ({ coverageStartDate, removalDates }) => {
  const startDateKey = coverageStartDate?.split('T')[0];
  const safeRemovalDates = Array.isArray(removalDates) ? removalDates : [];
  const cancellations = startDateKey
    ? safeRemovalDates.find(
      (removalDate) => removalDate.date?.split('T')[0] === startDateKey,
    )
    : null;

  return {
    cancelledCats: Number(cancellations?.cats) || 0,
    cancelledDogs: Number(cancellations?.dogs) || 0,
  };
};

/**
 * Minimum number of pets per species that must roll over into a term,
 * given how many cancellations are already expected for at that term's
 * start date. Eligible pets not covered by a cancellation have nowhere
 * else to go, so they're forced to roll over.
 */
const calculateRolloverMinimums = ({
  coverageStartDate,
  eligibleCats = 0,
  eligibleDogs = 0,
  numberOfCats = 0,
  numberOfDogs = 0,
  removalDates = [],
}) => {
  const { cancelledCats, cancelledDogs } = getCancelledCounts({
    coverageStartDate,
    removalDates,
  });

  const clampMinimum = (max, eligible, cancelled) => (
    Math.min(max, Math.max(0, eligible - cancelled))
  );

  return {
    minimumCats: clampMinimum(numberOfCats, eligibleCats, cancelledCats),
    minimumDogs: clampMinimum(numberOfDogs, eligibleDogs, cancelledDogs),
  };
};

const mapToEntries = (map) => (map instanceof Map ? [...map.entries()] : []);

const entriesToMap = (entries) => (
  new Map(Array.isArray(entries) ? entries : [])
);

const setToArray = (set) => (set instanceof Set ? [...set] : []);

const arrayToSet = (arr) => new Set(Array.isArray(arr) ? arr : []);

const readPersistedState = (key) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

export {
  arrayToSet,
  buildPetDemographicsBody,
  calculateRolloverMinimums,
  entriesToMap,
  getCancelledCounts,
  getPendingRemovalDates,
  mapToEntries,
  readPersistedState,
  setToArray,
};
